/* eslint-disable camelcase */
import { Component, Vue } from 'vue-property-decorator'
import { Nullable } from '@/shared/types/app.types'
import { CASE_STATES } from '@/api/resources/entityState/case-state'
import { CaseOperation } from '@/api/operations/models/case-operation'
import { SampleOperation } from '@/api/operations/models/sample-operation'
import sessionStore from '@/session/sessionStore'
import moment from 'moment'

@Component
export class CaseOperationGuardMixin extends Vue {
    /// VALIDATIONS ///

    $_isCaseDraft(caseStatusCode: Nullable<string>): boolean {
        return CASE_STATES.DRAFT.code === caseStatusCode
    }
    $_isCaseAlreadyInformed(caseStatusCode: Nullable<string>): boolean {
        if (!caseStatusCode) {
            return false
        }
        return [
            CASE_STATES.INFORMED.code,
            CASE_STATES.TRANSCRIBED.code,
            CASE_STATES.VALIDATED.code,
            CASE_STATES.IN_AMENDMENT.code
        ].includes(caseStatusCode)
    }
    $_isCaseValidated(caseStatusCode: Nullable<string>): boolean {
        if (!caseStatusCode) {
            return false
        }
        return [
            CASE_STATES.VALIDATED.code
        ].includes(caseStatusCode)
    }
    $_isCaseInAmendment(caseStatusCode: Nullable<string>): boolean {
        return CASE_STATES.IN_AMENDMENT.code === caseStatusCode
    }
    $_isCaseEditable(caseStatusCode: Nullable<string>): boolean {
        return !!caseStatusCode &&
            ![CASE_STATES.VALIDATED.code].includes(caseStatusCode)
    }

    /// GUARD OPERATIONS ///

    $_canCreateCase(): boolean {
        return this.$_hasOperationInCurrentLaboratory(CaseOperation.CREATE) &&
            this.$_hasOperationInCurrentLaboratory(SampleOperation.CREATE)
    }
    $_canEditCase(): boolean {
        return this.$_hasOperationInCurrentLaboratory(CaseOperation.EDIT)
    }
    $_canSaveCase(caseId: Nullable<number>): boolean {
        return !caseId
            ? this.$_canCreateCase()
            : this.$_canEditCase()
    }
    $_canDeleteCase(caseId: Nullable<number>, caseStatusCode: Nullable<string>): boolean {
        const isStateOk = !!caseId && !!caseStatusCode &&
            [CASE_STATES.DRAFT.code, CASE_STATES.REGISTERED.code].includes(caseStatusCode)
        return isStateOk &&
            this.$_hasOperationInCurrentLaboratory(CaseOperation.DELETE)
    }

    $_canPrintPreviousCaseReport(): boolean {
        return this.$_hasOperationInCurrentLaboratory(CaseOperation.PRINT)
    }
    $_canPrintCaseReport(laboratoryId?: Nullable<number>): boolean {
        if (laboratoryId) {
            return this.$_hasLabOperation(laboratoryId, CaseOperation.PRINT) ||
                this.$_hasLabOperation(laboratoryId, CaseOperation.PETITIONER_PRINT)
        }
        return this.$_hasOperationInCurrentLaboratory(CaseOperation.PRINT) ||
            this.$_hasOperationInCurrentLaboratory(CaseOperation.PETITIONER_PRINT)
    }
    $_canPrintCaseLabels(): boolean {
        return this.$_hasOperationInCurrentLaboratory(CaseOperation.PRINT_CASE_AND_SAMPLE_LABELS)
    }

    $_canAttachDocumentToCase(): boolean {
        return this.$_hasOperationInCurrentLaboratory(CaseOperation.ATTACH)
    }

    $_canRegisterCase(caseId: Nullable<number>, caseStatusCode: Nullable<string>): boolean {
        const isStateOk = !caseId || this.$_isCaseDraft(caseStatusCode)
        return isStateOk &&
            this.$_hasOperationInCurrentLaboratory(CaseOperation.CREATE) &&
            this.$_canEditCase()
    }
    $_canRegisterAndPrintCaseLabels(caseId: Nullable<number>, caseStatusCode: Nullable<string>): boolean {
        return this.$_canRegisterCase(caseId, caseStatusCode) &&
            this.$_hasOperationInCurrentLaboratory(CaseOperation.PRINT_CASE_AND_SAMPLE_LABELS)
    }

    $_canAssociateCases(): boolean {
        return this.$_hasOperationInCurrentLaboratory(CaseOperation.ASSOCIATE_CASES)
    }
    $_canReassignPatientToCase(): boolean {
        return this.$_hasOperationInCurrentLaboratory(CaseOperation.REASSIGN_PATIENT)
    }

    $_canInformCases(): boolean {
        return this.$_hasOperationInCurrentLaboratory(CaseOperation.INFORM)
    }

    $_canTranscribeToCase(caseStatusCode: Nullable<string>, caseTranscriptionRequired: Nullable<boolean>): boolean {
        const isStateOk = caseStatusCode === CASE_STATES.INFORMED.code &&
            !!caseTranscriptionRequired
        return isStateOk &&
            this.$_hasOperationInCurrentLaboratory(CaseOperation.TRANSCRIPT)
    }
    $_canValidateCases(): boolean {
        return this.$_hasOperationInCurrentLaboratory(CaseOperation.VALIDATE) ||
            this.$_hasOperationInCurrentLaboratory(CaseOperation.GLOBAL_VALIDATE)
    }

    $_canUserValidatePathologistCases(
        pathologistPersonnelId: Nullable<number>,
        pathologistAllowsCaseValidationByOtherUsers: Nullable<boolean>
    ): boolean {
        return (
            sessionStore.getUserPersonnelId() === pathologistPersonnelId &&
            this.$_hasOperationInCurrentLaboratory(CaseOperation.VALIDATE)
        ) || (
            !!pathologistAllowsCaseValidationByOtherUsers &&
            this.$_hasOperationInCurrentLaboratory(CaseOperation.GLOBAL_VALIDATE)
        )
    }
    $_canUserInvalidatePathologistCases(
        pathologistPersonnelId: Nullable<number>,
        pathologistAllowsCaseValidationByOtherUsers: Nullable<boolean>
    ): boolean {
        return (
            sessionStore.getUserPersonnelId() === pathologistPersonnelId &&
            this.$_hasOperationInCurrentLaboratory(CaseOperation.INVALIDATE)
        ) || (
            !!pathologistAllowsCaseValidationByOtherUsers &&
            this.$_hasOperationInCurrentLaboratory(CaseOperation.GLOBAL_INVALIDATE)
        )
    }

    $_canValidateIgnoringAssignedPathologistToCase(caseStudy: any): boolean {
        const isStateOk = [CASE_STATES.INFORMED.code, CASE_STATES.TRANSCRIBED.code].includes(caseStudy?.status.code)
        return isStateOk &&
            this.$_canUserValidatePathologistCases(
                caseStudy?.pathologist?.id,
                caseStudy?.pathologist?.allowsCaseValidationByOtherUsers)
    }
    $_canAmendToCase(caseStudy: any, invalidateMaxTimeInHoursAfterValidation: number): boolean {
        const isStateOk = caseStudy?.status.code === CASE_STATES.VALIDATED.code &&
            moment(caseStudy?.validationDate)
                .add(invalidateMaxTimeInHoursAfterValidation, 'hours')
                .isBefore(moment())
        return isStateOk &&
            this.$_canUserValidatePathologistCases(
                caseStudy?.pathologist?.id,
                caseStudy?.pathologist?.allowsCaseValidationByOtherUsers)
    }

    $_canEmitNotificationToCase(): boolean {
        return this.$_hasOperationInCurrentLaboratory(CaseOperation.EMIT_NOTIFICATION)
    }

    $_canViewAllDiagnose(): boolean {
        return this.$_hasOperationInCurrentLaboratory(CaseOperation.VIEW_ALL_DIAGNOSE)
    }

    /// CAN BE ///

    $_canBeCaseInformed(caseStudy: any): boolean {
        return caseStudy?.metadata?.operationGuards.canBeInformed
    }

    $_canBeCaseValidated(caseStudy: any): boolean {
        return caseStudy?.metadata?.operationGuards.canBeValidated
    }

    $_canBeCaseInvalidated(caseStudy: any): boolean {
        return caseStudy?.metadata?.operationGuards.canBeInvalidated
    }

    $_canBeCaseLabelsPrinted(caseStudy: any): boolean {
        return !!(caseStudy?.id || caseStudy?.casId)
    }

    $_canBeCaseReportPrinted(caseStudy: any): boolean {
        return !!(caseStudy?.id || caseStudy?.casId)
    }

    $_canBePatientReassignedToCase(caseStudy: any): boolean {
        return !this.$_isCaseValidated(caseStudy?.status.code)
    }

    $_canBeIncidentCreatedToCase(caseStudy: any): boolean {
        return caseStudy?.metadata?.operationGuards.canCreateIncidents
    }

    $_canBeDocumentAttachedToCase(caseStudy: any): boolean {
        return caseStudy?.metadata?.operationGuards.canBeAttached
    }

    $_canBeDocumentAttachedToNotValidatedCase(caseStudy: any): boolean {
        const isStateOk = !!(caseStudy?.id || caseStudy?.casId) &&
            !this.$_isCaseValidated(caseStudy?.status.code)
        return isStateOk &&
            this.$_canBeDocumentAttachedToCase(caseStudy)
    }

    /// DISABLED ///

    $_disabledValidateCases(caseStudies: any[]): boolean {
        return caseStudies?.some(caseStudy =>
            !this.$_canBeCaseValidated(caseStudy) ||
            !this.$_canValidateIgnoringAssignedPathologistToCase(caseStudy))
    }
}
